import groupBy from 'lodash/groupBy';
import isArray from 'lodash/isArray';
import isBoolean from 'lodash/isBoolean';
import keyBy from 'lodash/keyBy';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import factoryOptionsApi from '../api/factoryOptionsApi';
import roomsApi from '../api/roomsApi';
import selector from '../redux/reducers/factoryOptions/selectors';
import roomSelectors from '../redux/reducers/rooms/selectors';
import { useAuth } from './AuthProvider';

//---------------------------------------------------

const FactoryOptionsContext = createContext<any | null>(null);

interface FactoryOptionsProviderProps {
  children: React.ReactNode;
}

const FactoryOptionsProvider = ({ children }: FactoryOptionsProviderProps) => {
  const factoryOptionSelector = useSelector(selector.listUpdatingCount);
  const roomsSelector = useSelector(roomSelectors.rooms);
  const { isAuthentificated } = useAuth();

  const [factoryOptions, setFactoryOptions] = useState({});
  const [rooms, setFactoryRooms] = useState([]);

  const getOptions = useCallback(async () => {
    const [roomsRes, optionsRes] = await Promise.all([roomsApi.getInventoryRooms(), factoryOptionsApi.get()]);

    if (optionsRes.status === 200 && isArray(optionsRes.data?.data)) {
      const optionsObject = groupBy(optionsRes.data.data, 'optionName');
      const factoryConfigObject = keyBy(optionsObject?.factoryConfig, 'name');

      setFactoryOptions({
        ...optionsObject,
        ...factoryConfigObject,
      });
    }

    if (roomsRes.status === 200 && isArray(roomsRes.data?.data)) {
      setFactoryRooms(roomsRes.data.data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomsApi, factoryOptionsApi, setFactoryOptions, setFactoryRooms, factoryOptionSelector, roomsSelector]);

  useEffect(() => {
    if (isAuthentificated) {
      getOptions();
    }
  }, [isAuthentificated, getOptions]);

  const value = useMemo(
    () => ({
      factoryOptions,
      rooms,
      refetch: getOptions,
    }),
    [factoryOptions, getOptions, rooms]
  );

  return <FactoryOptionsContext.Provider value={value}>{children}</FactoryOptionsContext.Provider>;
};

export const useFactoryOptions = () => useContext(FactoryOptionsContext);

export const useFactoryConfigValue = (name) => {
  const { factoryOptions } = useFactoryOptions() || {};
  const configItem = factoryOptions ? factoryOptions[name] : null;

  return isBoolean(configItem?.value) ? configItem.value : configItem?.value || null;
};

export default FactoryOptionsProvider;
